import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { buildMarketCalibration, type MarketCalibration } from "@/lib/scoring-engine";

/**
 * The browser's copy of the calibration the orchestrator applies.
 *
 * The sensitivity grid feeds the global break detector, which can force a
 * NO-GO — so if the server stresses a San Antonio deal at −12% rent and the
 * browser stresses it at the standard −10%, the two can reach different
 * verdicts for the same deal. That is the two-engines bug this whole effort
 * removed, wearing a new coat.
 *
 * Row selection mirrors fetchMarketBenchmark in the edge runtime (metro ilike,
 * multifamily), reading through market_data_readable because the base table is
 * admin-only. The derivation itself is buildMarketCalibration, shared verbatim
 * with the server. Any failure yields null — the standard grid — which is the
 * same degradation the orchestrator applies.
 */
export async function fetchMarketCalibration(
  metro: string | null | undefined,
): Promise<MarketCalibration | null> {
  if (!metro || typeof metro !== "string" || !metro.trim()) return null;
  try {
    const { data, error } = await supabase
      .from("market_data_readable" as never)
      .select("metro, rent_growth_yoy, cap_rate, cap_rate_trend, rent_confidence")
      .ilike("metro", metro.trim())
      .eq("asset_type", "multifamily")
      .maybeSingle();
    if (error || !data) return null;
    return buildMarketCalibration(data as Record<string, unknown>);
  } catch {
    return null;
  }
}

/** Resolve the metro the same way the orchestrator does: context first. */
export function metroFromContext(ctx: Record<string, unknown> | null | undefined): string | null {
  if (!ctx) return null;
  const candidate = ctx.metro ?? ctx.market ?? null;
  return typeof candidate === "string" && candidate.trim() ? candidate.trim() : null;
}

export function useMarketCalibration(metro: string | null | undefined): MarketCalibration | null {
  const [calibration, setCalibration] = useState<MarketCalibration | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!metro) {
      setCalibration(null);
      return;
    }
    fetchMarketCalibration(metro).then((cal) => {
      if (!cancelled) setCalibration(cal);
    });
    return () => {
      cancelled = true;
    };
  }, [metro]);

  return calibration;
}
